import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";

function diff(target) {
    const now = Date.now();
    const ms = Math.max(0, target - now);
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((ms / (1000 * 60)) % 60);
    const seconds = Math.floor((ms / 1000) % 60);
    return { days, hours, minutes, seconds, expired: ms === 0 };
}

const Cell = ({ value, label }) => (
    <div className="flex flex-col items-center border border-white/10 px-4 sm:px-8 py-6 min-w-[80px] sm:min-w-[120px] bg-black/40 backdrop-blur-sm">
        <span className="font-display text-5xl sm:text-7xl lg:text-8xl leading-none tracking-tighter tabular-nums">
            {String(value).padStart(2, "0")}
        </span>
        <span className="label-mono mt-3 text-[9px] sm:text-[10px]">{label}</span>
    </div>
);

export default function Countdown() {
    const [drop, setDrop] = useState(null);
    const [t, setT] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: false });

    useEffect(() => {
        let mounted = true;
        api.get("/settings/next-drop")
            .then((res) => {
                if (mounted) setDrop(res.data);
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (!drop?.next_drop_at) return;
        const target = new Date(drop.next_drop_at).getTime();
        setT(diff(target));
        const id = setInterval(() => setT(diff(target)), 1000);
        return () => clearInterval(id);
    }, [drop]);

    if (!drop) return null;

    return (
        <section
            id="countdown"
            data-testid="countdown-section"
            className="relative bg-black border-y border-white/10 py-20 lg:py-28 overflow-hidden"
        >
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{
                    backgroundImage:
                        "url(https://images.unsplash.com/photo-1605602922835-a08452b4179d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            />
            <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12">
                <div className="grid grid-cols-12 gap-6 items-end mb-12">
                    <div className="col-span-12 md:col-span-5">
                        <p className="label-mono mb-4">— Next Drop</p>
                        <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-[0.85]">
                            {drop.title}
                        </h2>
                        <p className="font-sans text-muted-foreground mt-4 max-w-md">
                            {drop.subtitle}
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-7 md:text-right">
                        <p className="label-mono">Launches</p>
                        <p className="font-mono text-sm mt-2 text-white/70">
                            {new Date(drop.next_drop_at).toLocaleString("en-US", {
                                weekday: "short",
                                month: "short",
                                day: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </p>
                    </div>
                </div>

                {t.expired ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center py-12"
                    >
                        <p className="label-mono mb-4 text-signal">— Live Now</p>
                        <h3 className="font-display text-6xl md:text-8xl uppercase tracking-tighter">
                            The Drop
                            <br />
                            <span className="italic">is here.</span>
                        </h3>
                        <a
                            href="#shop"
                            className="inline-block mt-8 bg-white text-black px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] hover:bg-white/85"
                        >
                            Shop Now →
                        </a>
                    </motion.div>
                ) : (
                    <div
                        className="flex flex-wrap justify-center md:justify-start gap-3 sm:gap-4"
                        data-testid="countdown-timer"
                    >
                        <Cell value={t.days} label="Days" />
                        <Cell value={t.hours} label="Hours" />
                        <Cell value={t.minutes} label="Minutes" />
                        <Cell value={t.seconds} label="Seconds" />
                    </div>
                )}
            </div>
        </section>
    );
}
